import React, { useState, useEffect } from "react";
import { getAgingReport } from "./agingReportService";
import { FaChartBar } from "react-icons/fa";
import "./AgingReport.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const BUCKETS = ["0-30", "31-60", "61-90", "90+", "no_due"];
const BUCKET_LABELS = {
  "0-30": "1–30 Days",
  "31-60": "31–60 Days",
  "61-90": "61–90 Days",
  "90+": "90+ Days",
  "no_due": "No Due Date",
};
const BUCKET_COLORS = {
  "0-30": "#22c55e",
  "31-60": "#f59e0b",
  "61-90": "#f97316",
  "90+": "#ef4444",
  "no_due": "#64748b",
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const AgingReportPage = () => {
  const { canView } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  if (!canView("aging")) {
    return <AccessDenied moduleName="Aging Analysis" action="view" />;
  }
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(null); // 'weekly' | 'monthly' | null

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAgingReport(period);
        setSummary(data.summary);
      } catch (err) {
        setError("Failed to load aging report. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period]);

  const totalAmount = summary
    ? BUCKETS.reduce((acc, k) => acc + (summary[k]?.amount || 0), 0)
    : 0;

  return (
    <div className="aging-report-container">
      <div className="aging-report-header">
        <h1 className="aging-report-title">
          <FaChartBar className="me-2 text-primary" /> Aging Report – Summary
        </h1>
        <p className="aging-report-subtitle">
          Overdue invoice amounts grouped by aging buckets
        </p>
      </div>
      <div className="aging-period-selector">
        <select value={period || ''} onChange={(e) => setPeriod(e.target.value || null)} className="aging-period-select">
          <option value="">All</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {loading && (
        <div className="aging-loading">
          <div className="aging-spinner" />
          <span>Loading report...</span>
        </div>
      )}

      {error && <div className="aging-error">{error}</div>}

      {!loading && !error && summary && (
        <>
          {/* Summary Cards */}
          <div className="aging-cards-grid">
            {BUCKETS.map((bucket) => (
              <div
                key={bucket}
                className="aging-card"
                style={{ borderTop: `4px solid ${BUCKET_COLORS[bucket]}` }}
              >
                <div
                  className="aging-card-label"
                  style={{ color: BUCKET_COLORS[bucket] }}
                >
                  {BUCKET_LABELS[bucket]}
                </div>
                <div className="aging-card-count">
                  {summary[bucket]?.count ?? 0}{" "}
                  <span className="aging-card-count-label">invoices</span>
                </div>
                <div className="aging-card-amount">
                  AED {fmt(summary[bucket]?.amount)}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="aging-total-row">
            <span className="aging-total-label">Total Overdue Amount</span>
            <span className="aging-total-value">AED {fmt(totalAmount)}</span>
          </div>

          {/* Table */}
          <div className="aging-table-wrapper">
            <table className="aging-table">
              <thead>
                <tr>
                  <th>Aging Bucket</th>
                  <th>Invoice Count</th>
                  <th>Total Amount (AED)</th>
                  <th>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {BUCKETS.map((bucket) => {
                  const row = summary[bucket] || { count: 0, amount: 0 };
                  const pct =
                    totalAmount > 0
                      ? ((row.amount / totalAmount) * 100).toFixed(1)
                      : "0.0";
                  return (
                    <tr key={bucket}>
                      <td>
                        <span
                          className="aging-bucket-dot"
                          style={{ background: BUCKET_COLORS[bucket] }}
                        />
                        {BUCKET_LABELS[bucket]}
                      </td>
                      <td>{row.count}</td>
                      <td>{fmt(row.amount)}</td>
                      <td>
                        <div className="aging-bar-wrapper">
                          <div
                            className="aging-bar"
                            style={{
                              width: `${pct}%`,
                              background: BUCKET_COLORS[bucket],
                            }}
                          />
                          <span>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="aging-table-footer">
                  <td>Total</td>
                  <td>
                    {BUCKETS.reduce(
                      (acc, k) => acc + (summary[k]?.count || 0),
                      0
                    )}
                  </td>
                  <td>{fmt(totalAmount)}</td>
                  <td>100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AgingReportPage;
